/**
 * Shapes and helpers for the O/L CBA papers a student can sit.
 *
 * The API returns two different envelopes: list routes are flattened, and
 * everything else is nested under a response body. These readers keep that
 * detail in one place rather than at every call site.
 */

export type CbaPaperType = 'exam' | 'mock';

export type CbaLanguage = 'english' | 'sinhala' | 'tamil';

export const cbaLanguages: { value: CbaLanguage; label: string }[] = [
  { value: 'english', label: 'English' },
  { value: 'sinhala', label: 'Sinhala' },
  { value: 'tamil', label: 'Tamil' },
];

export interface CbaSubject {
  id: string;
  title: string;
  description?: string;
  paperCount: number;
}

export interface CbaAttemptInProgress {
  id: string;
  deadlineAt: string;
  remainingSeconds: number;
  /** Past its deadline but never closed, so it cannot be resumed. */
  expired: boolean;
}

export interface CbaPaperSummary {
  id: string;
  title: string;
  paperType: CbaPaperType;
  language: CbaLanguage;
  paperNumber: number;
  durationMinutes: number;
  mode: 'paged' | 'single';
  questionCount: number;
  subject?: { id: string; value: string };
  grade?: { id: string; value: string };
  attemptCount: number;
  bestScore?: number;
  bestPercentage?: number;
  lastAttemptAt?: string;
  inProgressAttempt?: CbaAttemptInProgress;
}

export interface CbaPaperDetail extends CbaPaperSummary {
  description?: string;
  warnAtMinutes: number[];
  passMark?: number;
  totalMarks: number;
  /** The server's clock, so the client can correct its own rather than trust the device. */
  serverNow: string;
}

/** Reads a route that nests its result under a response body. */
export const readBody = <T>(response: any): T => response?.response?.body as T;

/** Reads a route that flattens its result, the way the list routes do. */
export const readList = <T>(response: any): { data: T[]; total: number } => ({
  data: response?.data ?? [],
  total: response?.total ?? 0,
});

/** Minutes and seconds, as a student reads a clock. */
export const formatRemaining = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds || 0));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;

  return `${minutes}:${`${seconds}`.padStart(2, '0')}`;
};

/** How long a paper runs, in the words a student would use. */
export const formatDuration = (minutes: number): string => {
  const safe = Math.max(0, Math.round(minutes || 0));

  if (safe < 60) {
    return `${safe} minutes`;
  }

  const hours = Math.floor(safe / 60);
  const rest = safe % 60;
  const hourPart = `${hours} hour${hours === 1 ? '' : 's'}`;

  return rest === 0 ? hourPart : `${hourPart} ${rest} min`;
};

/** What the mock list says about this student's history with a paper. */
export const describeHistory = (paper: CbaPaperSummary): string => {
  if (paper.inProgressAttempt && !paper.inProgressAttempt.expired) {
    return 'You have a paper in progress';
  }

  if (!paper.attemptCount) {
    return 'Not attempted yet';
  }

  const attempts = `${paper.attemptCount} attempt${paper.attemptCount === 1 ? '' : 's'}`;

  return paper.bestPercentage === undefined
    ? attempts
    : `${attempts} · best ${Math.round(paper.bestPercentage)}%`;
};

/** What the button on a paper should say. */
export const startLabel = (paper: CbaPaperSummary | CbaPaperDetail): string => {
  if (paper.inProgressAttempt && !paper.inProgressAttempt.expired) {
    return 'Resume paper';
  }
  return paper.attemptCount > 0 ? 'Sit again' : 'Start paper';
};

/**
 * Whether a paper can be opened at all. A paper with no questions should never
 * be published, but the screen should not offer a dead button if one is.
 */
export const canSit = (paper: CbaPaperSummary | CbaPaperDetail): boolean =>
  (paper?.questionCount ?? 0) > 0;

/** One step in the trail the site header draws above a page. */
export interface Crumb {
  label: string;
  href: string;
}

/**
 * The trail down to a paper: its grade and subject when they are known, or the
 * papers home when they are not, then the paper and whatever follows it. The
 * header keys crumbs by `href`, so each one links somewhere different.
 */
export const paperCrumbs = (
  paper: Pick<CbaPaperSummary, 'id' | 'title' | 'subject' | 'grade'> | undefined,
  paperTitle: string,
  paperHref: string,
  ...rest: Crumb[]
): Crumb[] => {
  const subject = paper?.subject;
  const grade = paper?.grade;
  return [
    { label: 'O/L CBA Exam Papers', href: '/cba' },
    ...(grade ? [{ label: grade.value, href: `/cba?grade=${grade.id}` }] : []),
    subject
      ? { label: subject.value, href: `/cba?category=${subject.id}${grade ? `&grade=${grade.id}` : ''}` }
      : { label: '', href: '/cba' },
    { label: paper?.title ?? paperTitle, href: paperHref },
    ...rest,
  ];
};
